import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Popper,
  Paper,
  Toolbar,
  IconButton,
  Button,
  Box,
  useTheme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ClickAwayListener } from '@mui/material';

// Importation des contextes et hooks personnalisés
import { useAuth } from '../../contexts/AuthContext';
import { useAdminRole } from '../../hooks/useAdminRole';
import { updateUserProfile } from '../../firebase/userService';
import { Language } from '../../types/user';

// Importation des icônes
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AddchartIcon from '@mui/icons-material/Addchart';
import FaceIcon from '@mui/icons-material/Face';
import { RiMoonClearFill } from "react-icons/ri";
import { FaBook } from "react-icons/fa";
import { MdAnalytics } from "react-icons/md";
import { GrConnect } from "react-icons/gr";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import ChatIcon from '@mui/icons-material/Chat';
import PaidIcon from '@mui/icons-material/Paid';
import SettingsIcon from '@mui/icons-material/Settings';

interface NavbarProps {
  onLoginClick: () => void;
}

export default function Navbar({ onLoginClick }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, signout } = useAuth();
  const { isAdmin } = useAdminRole();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isDreamModalOpen, setIsDreamModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const languageModalAnchor = useRef<HTMLButtonElement | null>(null);
  const dreamModalAnchor = useRef<HTMLButtonElement | null>(null);
  const settingsModalAnchor = useRef<HTMLButtonElement | null>(null);
  const statsModalAnchor = useRef<HTMLButtonElement | null>(null);
  const profileModalAnchor = useRef<HTMLButtonElement | null>(null);

  const modalStyle = {
    background: 'var(--Pop-navbar)',
    mt: '0px',
    borderRadius: '10px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.94)',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  };

  // Effet pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = async (lng: Language) => {
    try {
      await i18n.changeLanguage(lng);
      if (currentUser) {
        await updateUserProfile(currentUser.uid, { language: lng });
      }
      setIsLanguageModalOpen(false);
    } catch (error) {
      console.error('Erreur lors du changement de langue:', error);
    }
  };

  const handleAuthClick = async () => {
    if (currentUser) {
      try {
        await signout();
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
      }
    } else {
      onLoginClick();
    }
  };

  const tooltipStyle = {
    tooltip: {
      sx: {
        background: 'var(--ConnectButton)',
        color: 'black',
        fontSize: '0.9rem',
        fontWeight: 500,
        borderRadius: '16px',
        padding: '4px 10px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
      }
    }
  };
  return (
    <Box sx={{}}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: isScrolled ? 'var(--navbar-background)' : 'transparent',
          boxShadow: isScrolled ? '0 2px 20px rgba(0, 0, 0, 0.9)' : 'none',
          top: '0px',
          left: '0px',
          right: '0px',
          maxWidth: 'calc(100% - 0px)',
          margin: '0 auto',
          transition: 'background 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '48px !important',
            '@media (min-width:600px)': {
              minHeight: '48px !important'
            },
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
        >
          {/* Section gauche avec logo et icônes principales */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.2, sm: 0.5 } }}>
            {/* Logo */}
            <Box
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                navigate('/');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginRight: { xs: 0.3, sm: 1 },
                flexShrink: 0, // Empêche le logo de rétrécir
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              <Box 
                component="img"
                src="/assets/home/logo2-mid.png"
                alt="LunaNuva Logo"
                sx={{                  
                  height: isMobile ? '32px' : '40px',
                  width: 'auto'
                }}
              />
            </Box>
            {/* Icônes individuelles pour utilisateur connecté */}
            {currentUser && (
              <>
                {/* Icône Nouveau rêve */}
                <Tooltip title={t('navbar.newDream')} placement="bottom" slotProps={tooltipStyle}>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/new-dream')}
                    sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}
                  >
                    <Box
                      component="img"
                      src="/assets/home/icon7.png"
                      alt="Statistics"
                      sx={{
                        width: { xs: '1.5rem', sm: '2rem' },
                        height: { xs: '1.5rem', sm: '2rem' },                              
                      }}
                    />
                  </IconButton>
                </Tooltip>

                {/* Icône Journal des rêves */}
                <Tooltip title={t('navbar.dreamJournal')} placement="bottom" slotProps={tooltipStyle}>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/dream-journal')}
                    sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}
                  >
                    <Box
                      component="img"
                      src="/assets/home/icon5.png"
                      alt="Statistics"
                      sx={{
                        width: { xs: '1.5rem', sm: '2rem' },
                        height: { xs: '1.5rem', sm: '2rem' },                              
                      }}
                    />
                  </IconButton>
                </Tooltip>

                {/* Icône Statistiques */}
                <Tooltip title={t('navbar.statistics')} placement="bottom" slotProps={tooltipStyle}>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/statistics')}
                    sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}
                  >
                    <Box
                      component="img"
                      src="/assets/home/icon6.png"
                      alt="Statistics"
                      sx={{
                        width: { xs: '1.8rem', sm: '2.5rem' },
                        height: { xs: '1.5rem', sm: '2rem' },                              
                      }}
                    />
                  </IconButton>
                </Tooltip>

                {/* Icône Galerie */}
                <Tooltip title={t('navbar.gallery')} placement="bottom" slotProps={tooltipStyle}>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/gallery')}
                    sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}
                  >
                    <Box
                      component="img"
                      src="/assets/home/icon2.png"
                      alt="Statistics"
                      sx={{
                        width: { xs: '1.5rem', sm: '2rem' },
                        height: { xs: '1.5rem', sm: '2rem' },                              
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Icône du Blog (toujours visible) */}
            <Tooltip title={t('navbar.blog')} placement="bottom" slotProps={tooltipStyle}>
              <IconButton
                color="inherit"
                onClick={() => navigate('/blog')}
                sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}
              >
                <Box
                  component="img"
                  src="/assets/home/icon1.png"
                  alt="Statistics"
                  sx={{
                    width: { xs: '1.5rem', sm: '2rem' },
                    height: { xs: '1.5rem', sm: '2rem' },                              
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Section droite avec profil et langue */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Version Desktop : Icônes séparées */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {/* Icône Profil (si utilisateur connecté) */}
            {currentUser && (
              <Tooltip title="User" placement="bottom" slotProps={tooltipStyle}>
                <div>
                  <ClickAwayListener onClickAway={() => setIsProfileModalOpen(false)}>
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        color="inherit"
                        onClick={() => setIsProfileModalOpen(!isProfileModalOpen)}
                        ref={profileModalAnchor}
                        sx={{ color: 'white', mr: -2 }}
                      >
                        <FaceIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>

                      <Popper
                        open={isProfileModalOpen}
                        anchorEl={profileModalAnchor.current}
                        placement="bottom"
                        sx={{ zIndex: 2001 }}
                      >
                        <Paper sx={modalStyle}>                       
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                              {t('navbar.pricing')}
                            </Typography>
                            <IconButton
                              onClick={() => {
                                navigate('/profile?pricing=1');
                                setIsProfileModalOpen(false);
                              }}
                              sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                            >
                              <PaidIcon sx={{ fontSize: '1.25rem' }} />
                            </IconButton>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                              {t('navbar.profile')}
                            </Typography>
                            <IconButton
                              onClick={() => {
                                navigate('/profile');
                                setIsProfileModalOpen(false);
                              }}
                              sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                            >
                              <AccountCircleIcon sx={{ fontSize: '1.25rem' }} />
                            </IconButton>
                          </Box>

                          {isAdmin && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                {t('navbar.admin')}
                              </Typography>
                              <IconButton
                                onClick={() => {
                                  navigate('/admin');
                                  setIsProfileModalOpen(false);
                                }}
                                sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                              >
                                <AdminPanelSettingsIcon sx={{ fontSize: '1.25rem' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Paper>
                      </Popper>
                    </Box>
                  </ClickAwayListener>
                </div>
              </Tooltip>
            )}

            {/* Icône pour la sélection de langue (toujours visible) */}
            <Tooltip title="" placement="bottom">
              <div>
                <ClickAwayListener onClickAway={() => setIsLanguageModalOpen(false)}>
                  <Box sx={{ position: 'relative' }}>
                    <IconButton
                      color="inherit"
                      onClick={() => setIsLanguageModalOpen(!isLanguageModalOpen)}
                      ref={languageModalAnchor}
                      sx={{ color: 'white', mr: -1 }}
                    >
                      <TranslateRoundedIcon sx={{ fontSize: '1.5rem' }} />
                    </IconButton>

                    <Popper
                      open={isLanguageModalOpen}
                      anchorEl={languageModalAnchor.current}
                      placement="bottom-end"
                      sx={{ zIndex: 1000 }}
                    >
                      <Paper sx={modalStyle}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                          <IconButton
                            onClick={() => changeLanguage('fr')}
                            sx={{ 
                              color: 'white', 
                              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              fontSize: '1rem',
                            }}
                          >
                            🇫🇷
                          </IconButton>
                          <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                            Français
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                          <IconButton
                            onClick={() => changeLanguage('en')}
                            sx={{ 
                              color: 'white', 
                              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              fontSize: '1rem',
                            }}
                          >
                            🇬🇧
                          </IconButton>
                          <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                            English
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                          <IconButton
                            onClick={() => changeLanguage('es')}
                            sx={{ 
                              color: 'white', 
                              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              fontSize: '1rem',
                            }}
                          >
                            🇪🇸
                          </IconButton>
                          <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                            Español
                          </Typography>
                        </Box>
                      </Paper>
                    </Popper>
                  </Box>
                </ClickAwayListener>
              </div>
            </Tooltip>

              {/* Bouton de Connexion/Déconnexion (plus petit) */}
              <Button
                onClick={handleAuthClick}
                sx={{
                  background: 'var(--ConnectButton)',
                  color: 'black',
                  borderRadius: '16px',
                  padding: isMobile ? '4px 8px' : '4px 10px',
                  minWidth: 'auto',
                  fontSize: '0.7rem',
                  height: '24px',
                  '&:hover': {
                    opacity: 0.5,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <GrConnect size="1rem" style={{ filter: 'brightness(0) invert(0)' }} />
                {!isMobile && <span>{currentUser ? t('navbar.logout') : t('navbar.login')}</span>}
              </Button>
            </Box>

            {/* Version Mobile : Menu Settings */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
              <Tooltip title="Settings" placement="bottom" slotProps={tooltipStyle}>
                <div>
                  <ClickAwayListener onClickAway={() => setIsSettingsModalOpen(false)}>
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        color="inherit"
                        onClick={() => setIsSettingsModalOpen(!isSettingsModalOpen)}
                        ref={settingsModalAnchor}
                        sx={{ color: 'white' }}
                      >
                        <SettingsIcon sx={{ fontSize: '1.5rem' }} />
                      </IconButton>

                      <Popper
                        open={isSettingsModalOpen}
                        anchorEl={settingsModalAnchor.current}
                        placement="bottom-end"
                        sx={{ zIndex: 2001 }}
                      >
                        <Paper sx={modalStyle}>
                          {/* Section Profil */}
                          {currentUser && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                  {t('navbar.pricing')}
                                </Typography>
                                <IconButton
                                  onClick={() => {
                                    navigate('/profile?pricing=1');
                                    setIsSettingsModalOpen(false);
                                  }}
                                  sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                                >
                                  <PaidIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                  {t('navbar.profile')}
                                </Typography>
                                <IconButton
                                  onClick={() => {
                                    navigate('/profile');
                                    setIsSettingsModalOpen(false);
                                  }}
                                  sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                                >
                                  <AccountCircleIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Box>

                              {isAdmin && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                    {t('navbar.admin')}
                                  </Typography>
                                  <IconButton
                                    onClick={() => {
                                      navigate('/admin');
                                      setIsSettingsModalOpen(false);
                                    }}
                                    sx={{ color: 'white', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)' }}
                                  >
                                    <AdminPanelSettingsIcon sx={{ fontSize: '1.25rem' }} />
                                  </IconButton>
                                </Box>
                              )}
                            </>
                          )}

                          {/* Séparateur */}
                          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', my: 1 }} />

                          {/* Section Langue */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <IconButton
                              onClick={() => {
                                changeLanguage('fr');
                                setIsSettingsModalOpen(false);
                              }}
                              sx={{ 
                                color: 'white', 
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                fontSize: '1rem',
                              }}
                            >
                              🇫🇷
                            </IconButton>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                              Français
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <IconButton
                              onClick={() => {
                                changeLanguage('en');
                                setIsSettingsModalOpen(false);
                              }}
                              sx={{ 
                                color: 'white', 
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                fontSize: '1rem',
                              }}
                            >
                              🇬🇧
                            </IconButton>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                              English
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <IconButton
                              onClick={() => {
                                changeLanguage('es');
                                setIsSettingsModalOpen(false);
                              }}
                              sx={{ 
                                color: 'white', 
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.49)',
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                fontSize: '1rem',
                              }}
                            >
                              🇪🇸
                            </IconButton>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                              Español
                            </Typography>
                          </Box>

                          {/* Séparateur */}
                          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', my: 1 }} />

                          {/* Section Connexion */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                            <Button
                              onClick={() => {
                                handleAuthClick();
                                setIsSettingsModalOpen(false);
                              }}
                              sx={{
                                background: 'var(--ConnectButton)',
                                color: 'black',
                                borderRadius: '16px',
                                padding: '6px 12px',
                                minWidth: 'auto',
                                fontSize: '0.8rem',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <GrConnect size="1rem" style={{ filter: 'brightness(0) invert(0)' }} />
                              <span>{currentUser ? t('navbar.logout') : t('navbar.login')}</span>
                            </Button>
                          </Box>
                        </Paper>
                      </Popper>
                    </Box>
                  </ClickAwayListener>
                </div>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
